import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import {
  FaPhoneAlt,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaHome,
  FaInfoCircle,
  FaTools,
  FaBriefcase,
  FaNewspaper,
} from "react-icons/fa";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scroll, setScroll] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScroll(window.scrollY > 30);
    };

    window.addEventListener("scroll", handleScroll);

    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    {
      name: "Home",
      path: "/",
    },
    {
      name: "About",
      path: "/about-us",
    },
    {
      name: "Services",
      path: "/services",
    },
    {
      name: "Projects",
      path: "/projects",
    },
    {
      name: "Blogs",
      path: "/blogs",
    },
    {
      name: "Contact",
      path: "/contact-us",
    },
  ];

  return (
    <>
      {/* Top Bar */}

      <div className="bg-primary text-white hidden lg:block">
        <div className="container-custom flex justify-between items-center py-2 text-sm">

          <div>
            Construction • Development • Property Dealer
          </div>

          <div className="flex items-center gap-6">
            <a href="tel:+919926666609">
              +91 99266 66609, +91 83195 69994
            </a>

            <div className="flex gap-4">
              <FaFacebookF />
              <FaInstagram />
              <FaLinkedinIn />
            </div>
          </div>

        </div>
      </div>

      {/* Main Navbar */}

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scroll
            ? "bg-white shadow-lg"
            : "bg-white"
        }`}
      >
        <div className="container-custom">

          <div className="flex items-center justify-between py-2">

            {/* Logo */}

            <Link
              to="/"
              className="flex items-center gap-3"
            >
              <img
                src="/logo.png"
                alt="AZ Construction"
                className="h-16 md:h-20 w-auto object-contain mix-blend-multiply"
              />
            </Link>

            {/* Desktop Menu */}

            <nav className="hidden lg:flex items-center gap-10">

              {navLinks.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `font-medium transition ${
                      isActive
                        ? "text-accent"
                        : "text-gray-700 hover:text-accent"
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}

            </nav>

            {/* CTA */}

            <div className="hidden lg:block">
              <a
                href="tel:+919926666609"
                className="bg-accent hover:bg-orange-600 transition text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold"
              >
                <FaPhoneAlt />
                Get Consultation
              </a>
            </div>

            {/* Mobile */}

            <button
              onClick={() => setOpen(true)}
              className="lg:hidden text-primary text-3xl"
            >
              <HiOutlineMenuAlt3 />
            </button>

          </div>

        </div>
      </header>

      {/* Overlay */}

      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 bg-black/50 z-[60] transition-all duration-300 ${
          open
            ? "opacity-100 visible"
            : "opacity-0 invisible"
        }`}
      />

      {/* Sidebar */}

      <div
        className={`fixed top-0 right-0 h-full w-[320px] bg-slate-950/95 backdrop-blur-2xl z-[70] shadow-2xl border-l border-white/10 transition-all duration-500 ease-out flex flex-col ${
          open
            ? "translate-x-0"
            : "translate-x-full"
        }`}
      >

        {/* Header */}

        <div className="p-6 flex justify-between items-center border-b border-white/5 bg-slate-900/50">

          <img
            src="/logo.png"
            alt="AZ Construction"
            className="h-14 w-auto bg-white p-2 rounded-xl object-contain shadow-md"
          />

          <button
            onClick={() => setOpen(false)}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white text-xl hover:bg-[#F59E0B] hover:text-white transition duration-300"
          >
            <IoClose />
          </button>

        </div>

        {/* Menu & Navigation Links */}

        <div className="flex-1 overflow-y-auto p-8 space-y-8">

          <div>
            <span className="text-white/40 uppercase tracking-widest text-xs font-semibold block mb-4">
              Navigation
            </span>

            <nav className="flex flex-col gap-3">
              {navLinks.map((item) => {
                let Icon = FaHome;
                if (item.name === "About") Icon = FaInfoCircle;
                if (item.name === "Services") Icon = FaTools;
                if (item.name === "Projects") Icon = FaBriefcase;
                if (item.name === "Blogs") Icon = FaNewspaper;
                if (item.name === "Contact") Icon = FaPhoneAlt;

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium transition duration-300 border border-transparent ${
                        isActive
                          ? "bg-[#F59E0B] text-white shadow-lg shadow-[#F59E0B]/20"
                          : "text-white/70 hover:text-white hover:bg-white/5 hover:border-white/5"
                      }`
                    }
                  >
                    <Icon className="text-lg opacity-85" />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* Quick CTA */}

          <div className="pt-2">
            <a
              href="tel:+919926666609"
              className="w-full bg-gradient-to-r from-[#F59E0B] to-amber-600 hover:from-amber-600 hover:to-orange-600 text-white py-4 rounded-xl flex justify-center items-center gap-2 font-semibold shadow-lg shadow-amber-600/10 transition duration-300"
            >
              <FaPhoneAlt />
              Call Now
            </a>
          </div>

          {/* Contact Details */}

          <div className="border-t border-white/5 pt-6 space-y-4">
            
            <span className="text-white/40 uppercase tracking-widest text-xs font-semibold block mb-2">
              Contact Info
            </span>

            <div className="space-y-4 text-sm text-white/70">
              
              <div className="flex gap-3 items-start">
                <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-xs text-[#F59E0B] flex-shrink-0">
                  📍
                </span>
                <p className="leading-6">
                  2, Gandhi Gram Colony, Near Khajrana Thana, Dargah Main Road, Khajrana Indore
                </p>
              </div>

              <div className="flex gap-3 items-center">
                <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-xs text-[#F59E0B] flex-shrink-0">
                  📞
                </span>
                <p>+91 99266 66609, +91 83195 69994</p>
              </div>

              <div className="flex gap-3 items-center">
                <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-xs text-[#F59E0B] flex-shrink-0">
                  ✉️
                </span>
                <p className="break-all text-xs select-all">contact@azconstruction.com</p>
              </div>

            </div>

          </div>

        </div>

        {/* Footer / Social Links */}

        <div className="p-6 border-t border-white/5 bg-slate-950 flex justify-between items-center">
          
          <span className="text-xs text-white/30 font-medium">
            © 2026 AZ Construction
          </span>

          <div className="flex gap-3">
            <a
              href="#"
              className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-[#F59E0B] hover:text-white transition duration-300"
            >
              <FaFacebookF className="text-sm" />
            </a>
            <a
              href="#"
              className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-[#F59E0B] hover:text-white transition duration-300"
            >
              <FaInstagram className="text-sm" />
            </a>
            <a
              href="#"
              className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-[#F59E0B] hover:text-white transition duration-300"
            >
              <FaLinkedinIn className="text-sm" />
            </a>
          </div>

        </div>

      </div>
    </>
  );
};

export default Navbar;